import { Routes } from '@angular/router';
import { guestGuard } from './Modules/Shared/Guards/guest.guard';
import { loggedInGuard } from './Modules/Shared/Guards/logged-in.guard';
import { roleGuard } from './Modules/Shared/Guards/role.guard';
import { AuthComponent } from './Modules/Auth/auth/auth.component';
import { LoginComponent } from './Modules/Auth/Components/Pages/login/login.component';
import { CallbackComponent } from './Modules/Auth/Components/Pages/callback/callback.component';
import { ShellComponent } from './Modules/Core/shell/shell.component';
import { DashboardComponent } from './Modules/Dashboard/Components/Pages/dashboard/dashboard.component';
import { OrganizationsComponent } from './Modules/Organizations/Components/Pages/organizations/organizations.component';
import { OrganizationDetailComponent } from './Modules/Organizations/Components/Pages/organization-detail/organization-detail.component';
import { SubscriptionControlComponent } from './Modules/Subscriptions/Components/Pages/subscription-control/subscription-control.component';
import { BillingDocumentsComponent } from './Modules/Billing/Components/Pages/billing-documents/billing-documents.component';
import { InvoiceDetailComponent } from './Modules/Billing/Components/Pages/invoice-detail/invoice-detail.component';
import { LicencePortfolioComponent } from './Modules/Licences/Components/Pages/licence-portfolio/licence-portfolio.component';
import { DeviceEstateComponent } from './Modules/Devices/Components/Pages/device-estate/device-estate.component';
import { ReconciliationComponent } from './Modules/Reconciliation/Components/Pages/reconciliation/reconciliation.component';
import { PlansComponent } from './Modules/Plans/Components/Pages/plans/plans.component';
import { PlanDetailComponent } from './Modules/Plans/Components/Pages/plan-detail/plan-detail.component';
import { UsersComponent } from './Modules/Users/Components/Pages/users/users.component';
import { UserDetailComponent } from './Modules/Users/Components/Pages/user-detail/user-detail.component';
import { TeamComponent } from './Modules/Users/Components/Pages/team/team.component';
import { OpsHealthComponent } from './Modules/Ops/Components/Pages/ops-health/ops-health.component';
import { WebhookEventsComponent } from './Modules/Ops/Components/Pages/webhook-events/webhook-events.component';
import { AuditLogComponent } from './Modules/Ops/Components/Pages/audit-log/audit-log.component';
import { SettingsComponent } from './Modules/Settings/Components/Pages/settings/settings.component';
import { OnboardingsComponent } from './Modules/Onboardings/Components/Pages/onboardings/onboardings.component';
import { RbacComponent } from './Modules/Rbac/Components/Pages/rbac/rbac.component';
import { DashboardFacade } from './Facades/Dashboard/dashboard.facade';
import { OrganizationsFacade } from './Facades/Organizations/organizations.facade';
import { OrganizationDetailFacade } from './Facades/Organizations/organization-detail.facade';
import { OrganizationBillingFacade } from './Facades/Organizations/organization-billing.facade';
import { OrganizationLicenceFacade } from './Facades/Organizations/organization-licence.facade';
import { OrganizationDeviceFacade } from './Facades/Organizations/organization-device.facade';
import { SubscriptionControlFacade } from './Facades/Subscriptions/subscription-control.facade';
import { BillingDocumentsFacade } from './Facades/Billing/billing-documents.facade';
import { InvoiceDetailFacade } from './Facades/Billing/invoice-detail.facade';
import { LicencePortfolioFacade } from './Facades/Licences/licence-portfolio.facade';
import { DeviceEstateFacade } from './Facades/Devices/device-estate.facade';
import { ReconciliationFacade } from './Facades/Reconciliation/reconciliation.facade';
import { PlansFacade } from './Facades/Plans/plans.facade';
import { PlanDetailFacade } from './Facades/Plans/plan-detail.facade';
import { UsersFacade } from './Facades/Users/users.facade';
import { UserDetailFacade } from './Facades/Users/user-detail.facade';
import { TeamFacade } from './Facades/Users/team.facade';
import { OpsFacade } from './Facades/Ops/ops.facade';
import { WebhookEventsFacade } from './Facades/Ops/webhook-events.facade';
import { AuditLogFacade } from './Facades/Ops/audit-log.facade';
import { AlertSettingsFacade } from './Facades/Settings/alert-settings.facade';
import { OnboardingsFacade } from './Facades/Onboardings/onboardings.facade';
import { RbacFacade } from './Facades/Rbac/rbac.facade';
import { SystemRole } from './Enums/system-role.enum';

export const routes: Routes = [
  {
    path: 'auth',
    component: AuthComponent,
    canActivate: [guestGuard],
    children: [
      { path: 'login', component: LoginComponent, title: 'backoffice.titles.login' },
      { path: 'callback', component: CallbackComponent, title: 'backoffice.titles.callback' },
      { path: '', redirectTo: 'login', pathMatch: 'full' },
      { path: '**', redirectTo: 'login', pathMatch: 'full' },
    ],
  },
  {
    path: '',
    component: ShellComponent,
    canActivate: [loggedInGuard],
    children: [
      {
        path: '',
        component: DashboardComponent,
        canActivate: [roleGuard],
        data: { minRole: SystemRole.BACKOFFICE_VIEWER },
        providers: [DashboardFacade],
        title: 'backoffice.titles.dashboard',
        pathMatch: 'full',
      },
      {
        path: 'organizations',
        component: OrganizationsComponent,
        canActivate: [roleGuard],
        data: { minRole: SystemRole.BACKOFFICE_VIEWER },
        providers: [OrganizationsFacade],
        title: 'backoffice.titles.organizations',
      },
      {
        path: 'organizations/:id',
        component: OrganizationDetailComponent,
        canActivate: [roleGuard],
        data: { minRole: SystemRole.BACKOFFICE_VIEWER },
        providers: [
          OrganizationDetailFacade,
          OrganizationBillingFacade,
          OrganizationLicenceFacade,
          OrganizationDeviceFacade,
        ],
        title: 'backoffice.titles.organization',
      },
      {
        path: 'organizations/:id/subscription',
        component: SubscriptionControlComponent,
        canActivate: [roleGuard],
        data: { minRole: SystemRole.BACKOFFICE_VIEWER },
        providers: [SubscriptionControlFacade],
        title: 'backoffice.titles.subscription_control',
      },
      {
        path: 'organizations/:id/billing',
        component: BillingDocumentsComponent,
        canActivate: [roleGuard],
        data: { minRole: SystemRole.BACKOFFICE_VIEWER },
        providers: [BillingDocumentsFacade],
        title: 'backoffice.titles.billing_documents',
      },
      {
        path: 'organizations/:id/billing/invoices/:invoiceId',
        component: InvoiceDetailComponent,
        canActivate: [roleGuard],
        data: { minRole: SystemRole.BACKOFFICE_VIEWER },
        providers: [InvoiceDetailFacade],
        title: 'backoffice.titles.invoice',
      },
      {
        path: 'licences',
        component: LicencePortfolioComponent,
        canActivate: [roleGuard],
        data: { minRole: SystemRole.BACKOFFICE_VIEWER },
        providers: [LicencePortfolioFacade],
        title: 'backoffice.titles.licences',
      },
      {
        path: 'devices',
        component: DeviceEstateComponent,
        canActivate: [roleGuard],
        data: { minRole: SystemRole.BACKOFFICE_VIEWER },
        providers: [DeviceEstateFacade],
        title: 'backoffice.titles.devices',
      },
      {
        path: 'reconciliation',
        component: ReconciliationComponent,
        canActivate: [roleGuard],
        data: { minRole: SystemRole.BACKOFFICE_VIEWER },
        providers: [ReconciliationFacade],
        title: 'backoffice.titles.reconciliation',
      },
      {
        path: 'plans',
        component: PlansComponent,
        canActivate: [roleGuard],
        data: { minRole: SystemRole.BACKOFFICE_VIEWER },
        providers: [PlansFacade],
        title: 'backoffice.titles.plans',
      },
      {
        path: 'plans/:id',
        component: PlanDetailComponent,
        canActivate: [roleGuard],
        data: { minRole: SystemRole.BACKOFFICE_VIEWER },
        providers: [PlanDetailFacade],
        title: 'backoffice.titles.plan',
      },
      {
        path: 'users',
        component: UsersComponent,
        canActivate: [roleGuard],
        data: { minRole: SystemRole.BACKOFFICE_AGENT },
        providers: [UsersFacade],
        title: 'backoffice.titles.users',
      },
      {
        path: 'users/:id',
        component: UserDetailComponent,
        canActivate: [roleGuard],
        data: { minRole: SystemRole.BACKOFFICE_AGENT },
        providers: [UserDetailFacade],
        title: 'backoffice.titles.user_detail',
      },
      {
        path: 'team',
        component: TeamComponent,
        canActivate: [roleGuard],
        data: { minRole: SystemRole.BACKOFFICE_ADMIN },
        providers: [TeamFacade],
        title: 'backoffice.titles.team',
      },
      {
        path: 'rbac',
        component: RbacComponent,
        canActivate: [roleGuard],
        data: { minRole: SystemRole.BACKOFFICE_VIEWER },
        providers: [RbacFacade],
        title: 'backoffice.titles.rbac',
      },
      {
        path: 'ops',
        component: OpsHealthComponent,
        canActivate: [roleGuard],
        data: { minRole: SystemRole.BACKOFFICE_VIEWER },
        providers: [OpsFacade],
        title: 'backoffice.titles.ops',
      },
      {
        path: 'webhooks',
        component: WebhookEventsComponent,
        canActivate: [roleGuard],
        data: { minRole: SystemRole.BACKOFFICE_VIEWER },
        providers: [WebhookEventsFacade],
        title: 'backoffice.titles.webhooks',
      },
      {
        path: 'audit',
        component: AuditLogComponent,
        canActivate: [roleGuard],
        data: { minRole: SystemRole.BACKOFFICE_VIEWER },
        providers: [AuditLogFacade],
        title: 'backoffice.titles.audit',
      },
      {
        path: 'onboardings',
        component: OnboardingsComponent,
        canActivate: [roleGuard],
        data: { minRole: SystemRole.BACKOFFICE_VIEWER },
        providers: [OnboardingsFacade],
        title: 'backoffice.titles.onboardings',
      },
      {
        path: 'settings',
        component: SettingsComponent,
        canActivate: [roleGuard],
        data: { minRole: SystemRole.BACKOFFICE_VIEWER },
        providers: [AlertSettingsFacade],
        title: 'backoffice.titles.settings',
      },
      { path: '**', redirectTo: '', pathMatch: 'full' },
    ],
  },
  { path: '**', redirectTo: '', pathMatch: 'full' },
];
